import { useState, useEffect } from 'react';
import { api, Credential } from '../lib/api';

const providerLabels: Record<string, { name: string; color: string }> = {
  stripe: { name: 'Stripe', color: 'bg-purple-100 text-purple-800' },
  murf: { name: 'Murf.ai', color: 'bg-blue-100 text-blue-800' },
  elevenlabs: { name: 'ElevenLabs', color: 'bg-green-100 text-green-800' },
  openai: { name: 'OpenAI', color: 'bg-teal-100 text-teal-800' },
  aws: { name: 'AWS', color: 'bg-orange-100 text-orange-800' },
};

export function Credentials() {
  const [credentialsByProvider, setCredentialsByProvider] = useState<Record<string, Credential[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadCredentials();
  }, []);

  const loadCredentials = async () => {
    setLoading(true);
    const result = await api.getCredentials();
    if (result.success && result.data) {
      setCredentialsByProvider(result.data.by_provider);
    } else {
      setError(result.error?.message || 'Failed to load credentials');
    }
    setLoading(false);
  };

  const handleEdit = (keyName: string) => {
    setEditingKey(keyName);
    setEditValue('');
    setMessage(null);
  };

  const handleSave = async () => {
    if (!editingKey || !editValue.trim()) return;

    setSaving(true);
    const result = await api.setCredential(editingKey, editValue.trim());
    if (result.success) {
      setMessage({ type: 'success', text: 'Credential saved successfully' });
      setEditingKey(null);
      setEditValue('');
      await loadCredentials();
    } else {
      setMessage({ type: 'error', text: result.error?.message || 'Failed to save credential' });
    }
    setSaving(false);
  };

  const handleClear = async (keyName: string) => {
    if (!confirm('Are you sure you want to clear this credential?')) return;

    const result = await api.clearCredential(keyName);
    if (result.success) {
      setMessage({ type: 'success', text: 'Credential cleared' });
      await loadCredentials();
    } else {
      setMessage({ type: 'error', text: result.error?.message || 'Failed to clear credential' });
    }
  };

  const handleVerify = async (provider: string) => {
    setVerifying(provider);
    setMessage(null);

    const result = await api.verifyProvider(provider);
    if (result.success) {
      setMessage({ type: 'success', text: result.data?.message || 'Credentials verified' });
    } else {
      setMessage({ type: 'error', text: result.error?.message || 'Verification failed' });
    }
    await loadCredentials();
    setVerifying(null);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'valid':
        return <span className="badge badge-success">Valid</span>;
      case 'invalid':
        return <span className="badge badge-danger">Invalid</span>;
      default:
        return <span className="badge badge-gray">Pending</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card text-center py-12">
        <p className="text-red-600">{error}</p>
        <button onClick={loadCredentials} className="btn btn-primary mt-4">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">API Credentials</h1>
          <p className="text-gray-600 mt-1">Manage external API keys for payment and TTS providers</p>
        </div>
        <button onClick={loadCredentials} className="btn btn-secondary">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
          {message.text}
        </div>
      )}

      <div className="space-y-6">
        {Object.entries(credentialsByProvider).map(([provider, credentials]) => (
          <div key={provider} className="card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-semibold text-gray-900">
                  {providerLabels[provider]?.name || provider}
                </h2>
                <span className={`badge ${providerLabels[provider]?.color || 'badge-gray'}`}>
                  {credentials.filter(c => c.is_configured).length}/{credentials.length} configured
                </span>
              </div>
              {['stripe', 'murf', 'elevenlabs', 'openai'].includes(provider) && (
                <button
                  onClick={() => handleVerify(provider)}
                  disabled={verifying === provider}
                  className="btn btn-outline text-sm"
                >
                  {verifying === provider ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Verifying...
                    </>
                  ) : (
                    'Verify Credentials'
                  )}
                </button>
              )}
            </div>

            <div className="divide-y divide-gray-100">
              {credentials.map((cred) => (
                <div key={cred.id} className="py-4 first:pt-0 last:pb-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-gray-900">{cred.display_name}</h3>
                        {cred.is_required && (
                          <span className="text-xs text-red-500">Required</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">{cred.description}</p>
                      <div className="flex items-center gap-3 mt-2">
                        {cred.is_configured ? (
                          <>
                            <code className="text-sm bg-gray-100 px-2 py-0.5 rounded font-mono">
                              {cred.masked_value || '********'}
                            </code>
                            {getStatusBadge(cred.verification_status)}
                          </>
                        ) : (
                          <span className="text-sm text-yellow-600 bg-yellow-50 px-2 py-0.5 rounded">
                            Not configured
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {editingKey === cred.key_name ? (
                        <>
                          <input
                            type="password"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            placeholder="Enter value..."
                            className="input w-64"
                            autoFocus
                          />
                          <button
                            onClick={handleSave}
                            disabled={saving || !editValue.trim()}
                            className="btn btn-primary"
                          >
                            {saving ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={() => setEditingKey(null)}
                            className="btn btn-secondary"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEdit(cred.key_name)}
                            className="btn btn-secondary text-sm"
                          >
                            {cred.is_configured ? 'Update' : 'Configure'}
                          </button>
                          {cred.is_configured && (
                            <button
                              onClick={() => handleClear(cred.key_name)}
                              className="btn btn-outline text-sm text-red-600 hover:bg-red-50"
                            >
                              Clear
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
